import React from "react";

import { IPictoProps, Picto } from "@amphore-dev/amphore-lib";
import { Link, LinkProps } from "react-router-dom";

import { useIsRouteActive } from "@/hooks/useIsRouteActive";

import { cn } from "@/utils/cn";

import { TRoute } from "@/types";

export interface INavBarItemProps
	extends Omit<LinkProps, "children">, Omit<TRoute, "title"> {
	picto?: IPictoProps["icon"];
	/** Drawn in the picto's place (the "Me" tab's avatar). */
	icon?: React.ReactNode;
	/** A dot on the icon: something there needs a look. */
	badge?: boolean;
	isReduced?: boolean;
	children?: React.ReactNode;
}

/**
 * One tab of the mobile nav bar: icon in a pill that fills when active,
 * label under it. Active on every path the entry stands for, not only
 * its `to`.
 */
export const NavBarItem: React.FC<INavBarItemProps> = ({
	to,
	path,
	children,
	picto,
	icon,
	badge,
	isReduced,
}) => {
	const isActive = useIsRouteActive(path ?? String(to));
	return (
		<Link
			to={to}
			aria-current={isActive ? "page" : undefined}
			className={cn([
				"flex min-w-0 flex-1 flex-col items-center gap-0.5 py-1 text-[11px] leading-tight no-underline!",
				isActive
					? "text-primary-700 font-semibold"
					: "font-medium text-neutral-600",
			])}
		>
			<span
				className={cn([
					"relative flex h-8 w-14 items-center justify-center rounded-full",
					isActive && !icon && "bg-primary-50",
				])}
			>
				{icon ?? (picto && <Picto icon={picto} className="h-6 w-6" />)}
				{badge && (
					<span
						className="absolute top-0.5 right-3 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500"
						data-testid="navbar-item-badge"
					/>
				)}
			</span>
			{!isReduced && (
				<span className="max-w-full truncate">{children}</span>
			)}
		</Link>
	);
};
